import React from "react";
import { useIntl } from "react-intl";

import { TranslationsProvider } from "@learningblocks/simulations/shared-components";

import { fetchTranslations } from "./locales/config";
import { SliderWidget } from "@learningblocks/storybook-components";

const SliderImage = (props) => {
  const intl = useIntl();
  const [value, onChange] = React.useState(0);

  function getSliderAltText(animation, value) {
    switch (animation) {
      case "CARBON_DIOXIDE_SLIDER":
        return intl.formatMessage(
          {
            id: "At {value} seconds, the pH value of the solution is {carbonSliderAltTextPh}, and the color of the solution is {carbonSliderAltTextColour}.",
            defaultMessage:
              "At {value} seconds, the pH value of the solution is {carbonSliderAltTextPh}, and the color of the solution is {carbonSliderAltTextColour}.",
            description: "",
          },
          {
            value: value,
            carbonSliderAltTextPh: getCarbonSliderAltTextPh(value),
            carbonSliderAltTextColour: getCarbonSliderAltTextColour(value),
          }
        );

      case "LIGHT_DURATION_SLIDER":
        return intl.formatMessage(
          {
            id: "At {value} minutes, the pH value is {lightSliderAltTextPh}, the solution is {lightSliderAltTextColour}, and there is {lightSliderAltTextDescription}.",
            defaultMessage:
              "At {value} minutes, the pH value is {lightSliderAltTextPh}, the solution is {lightSliderAltTextColour}, and there is {lightSliderAltTextDescription}.",
            description: "",
          },
          {
            value: value >= 12 ? 660 : value * 60,
            lightSliderAltTextPh: getLightSliderAltTextPh(value),
            lightSliderAltTextColour: getLightSliderAltTextColour(value),
            lightSliderAltTextDescription:
              getLightSliderAltTextDescription(value),
          }
        );

      case "ABSORBANCE_SLIDER":
        return intl.formatMessage(
          {
            id: "Drag the slider between absorbance values 0.11 to 1 to see the change in pH value. \nAt absorbance {value}, the solution is {absorbanceSliderAltTextColour}, and the pH value is {absorbanceSliderAltTextPh}.",
            defaultMessage:
              "Drag the slider between absorbance values 0.11 to 1 to see the change in pH value. \nAt absorbance {value}, the solution is {absorbanceSliderAltTextColour}, and the pH value is {absorbanceSliderAltTextPh}.",
            description: "",
          },
          {
            value: value,
            absorbanceSliderAltTextColour:
              getAbsorbanceliderAltTextColour(value),
            absorbanceSliderAltTextPh: getAbsorbanceliderAltTextPh(value),
          }
        );

      case "PH_SLIDER":
        return intl.formatMessage(
          {
            id: "At pH {value}, the solution is {phSliderAltColourDescription}, and the absorbance is {phSliderAltAbsorbanceDescription}.",
            defaultMessage:
              "At pH {value}, the solution is {phSliderAltColourDescription}, and the absorbance is {phSliderAltAbsorbanceDescription}.",
            description: "",
          },
          {
            value: value / 12.5,
            phSliderAltColourDescription:
              getPhSliderAltColourDescription(value),
            phSliderAltAbsorbanceDescription:
              getPhSliderAltAbsorbanceDescription(value),
          }
        );

      default:
        return animation;
    }
  }

  function getPhSliderAltAbsorbanceDescription(value) {
    switch (value) {
      case 0:
        return "0.14";

      case 87.5:
        return "0.57";

      case 175:
        return "0.85";

      default:
        break;
    }
  }

  function getPhSliderAltColourDescription(value) {
    switch (value) {
      case 0:
        return intl.formatMessage({
          id: "yellow",
          defaultMessage: "yellow",
          description: "",
        });

      case 87.5:
        return intl.formatMessage({
          id: "green",
          defaultMessage: "green",
          description: "",
        });

      case 175:
        return intl.formatMessage({
          id: "blue",
          defaultMessage: "blue",
          description: "",
        });

      default:
        break;
    }
  }

  function getLightSliderAltTextDescription(time) {
    let value = time * 60;
    value = value > 660 ? 660 : value;
    console.log(value);

    if (value <= 0)
      return intl.formatMessage({
        id: "Far less oxygen than there is carbon dioxide",
        defaultMessage: "Far less oxygen than there is carbon dioxide",
        description: "",
      });

    if (value <= 60)
      return intl.formatMessage({
        id: "Three times less oxygen than there is carbon dioxide",
        defaultMessage: "Three times less oxygen than there is carbon dioxide",
        description: "",
      });

    if (value <= 120)
      return intl.formatMessage({
        id: "Nearly as much oxygen as there is carbon dioxide",
        defaultMessage: "Nearly as much oxygen as there is carbon dioxide",
        description: "",
      });

    if (value <= 180)
      return intl.formatMessage({
        id: "Slightly more oxygen than there is carbon dioxide",
        defaultMessage: "Slightly more oxygen than there is carbon dioxide",
        description: "",
      });

    if (value <= 240)
      return intl.formatMessage({
        id: "Three times more oxygen than there is carbon dioxide",
        defaultMessage: "Three times more oxygen than there is carbon dioxide",
        description: "",
      });

    if (value <= 300)
      return intl.formatMessage({
        id: "Five times more oxygen than there is carbon dioxide",
        defaultMessage: "Five times more oxygen than there is carbon dioxide",
        description: "",
      });

    if (value <= 660)
      return intl.formatMessage({
        id: "Much more oxygen than there is carbon dioxide",
        defaultMessage: "Much more oxygen than there is carbon dioxide",
        description: "",
      });
  }

  function getLightSliderAltTextColour(time) {
    const value = time * 60;
    if (value <= 0) return;
    intl.formatMessage({
      id: "Greenish-yellow",
      defaultMessage: "Greenish-yellow",
      description: "",
    });

    if (value <= 60)
      return intl.formatMessage({
        id: "Muddy green",
        defaultMessage: "Muddy green",
        description: "",
      });

    if (value <= 120)
      return intl.formatMessage({
        id: "Deep teal",
        defaultMessage: "Deep teal",
        description: "",
      });

    if (value <= 180)
      return intl.formatMessage({
        id: "Deep, muddy blue",
        defaultMessage: "Deep, muddy blue",
        description: "",
      });

    if (value <= 240)
      return intl.formatMessage({
        id: "Dark, muddy blue, bordering on navy",
        defaultMessage: "Dark, muddy blue, bordering on navy",
        description: "",
      });

    if (value <= 300)
      return intl.formatMessage({
        id: "Dark, muddy navy",
        defaultMessage: "Dark, muddy navy",
        description: "",
      });

    if (value <= 660)
      return intl.formatMessage({
        id: "Dark navy",
        defaultMessage: "Dark navy",
        description: "",
      });
  }

  function getLightSliderAltTextPh(time) {
    const value = time * 60;
    if (value <= 0) return "6.7";

    if (value <= 60) return "7.1";

    if (value <= 120) return "7.4";

    if (value <= 180) return "7.7";

    if (value <= 240) return "7.9";

    if (value <= 660) return "8.0";
  }

  function getAbsorbanceliderAltTextColour(value) {
    if (value < 30)
      return intl.formatMessage({
        id: "Yellow",
        defaultMessage: "Yellow",
        description: "",
      });

    if (value < 40)
      return intl.formatMessage({
        id: "Yellowish green",
        defaultMessage: "Yellowish green",
        description: "",
      });

    if (value < 60)
      return intl.formatMessage({
        id: "Green",
        defaultMessage: "Green",
        description: "",
      });

    if (value < 70)
      return intl.formatMessage({
        id: "Bluish green",
        defaultMessage: "Bluish green",
        description: "",
      });

    if (value < 80)
      return intl.formatMessage({
        id: "Greenish blue",
        defaultMessage: "Greenish blue",
        description: "",
      });

    if (value < 90)
      return intl.formatMessage({
        id: "Blue",
        defaultMessage: "Blue",
        description: "",
      });

    if (value < 100)
      return intl.formatMessage({
        id: "Navy blue",
        defaultMessage: "Navy blue",
        description: "",
      });
  }

  function getAbsorbanceliderAltTextPh(value) {
    if (value < 0.11) return "6.0";

    if (value < 0.2) return "6.2";

    if (value < 0.3) return "6.4";

    if (value < 0.4) return "6.7";

    if (value < 0.5) return "6.9";

    if (value < 0.6) return "7.1";

    if (value < 0.7) return "7.3";

    if (value < 0.8) return "7.5";

    if (value < 0.9) return "7.8";

    if (value < 1) return "8.0";
  }

  function getCarbonSliderAltTextColour(time) {
    switch (time) {
      case 0:
      case 1:
      case 2:
      case 3:
        return intl.formatMessage({
          id: "Deep blue",
          defaultMessage: "Deep blue",
          description: "",
        });
      case 4:
        return intl.formatMessage({
          id: "Deep greenish-blue",
          defaultMessage: "Deep greenish-blue",
          description: "",
        });

      case 5:
        return intl.formatMessage({
          id: "Dark green",
          defaultMessage: "Dark green",
          description: "",
        });

      case 6:
        return intl.formatMessage({
          id: "Khaki green",
          defaultMessage: "Khaki green",
          description: "",
        });

      case 7:
      case 8:
        return intl.formatMessage({
          id: "Medium green",
          defaultMessage: "Medium green",
          description: "",
        });

      case 9:
        return intl.formatMessage({
          id: "Green",
          defaultMessage: "Green",
          description: "",
        });

      case 10:
        return intl.formatMessage({
          id: "Greenish-yellow",
          defaultMessage: "Greenish-yellow",
          description: "",
        });
      default:
        break;
    }
  }

  function getCarbonSliderAltTextPh(time) {
    switch (time) {
      case 0:
        return "8.0";

      case 1:
        return "7.9";

      case 2:
        return "7.7";

      case 3:
        return "7.5";

      case 4:
        return "7.4";

      case 5:
        return "7.3";

      case 6:
        return "7.1";

      case 7:
        return "6.9";

      case 8:
        return "6.7";

      case 9:
        return "6.5";

      case 10:
        return "6.5";

      default:
        break;
    }
  }

  return (
    <SliderWidget
      {...props}
      key={props.id}
      value={value}
      onChange={onChange}
      animeAriaLabel={props.sliderAnimationAltText}
      sliderAriaLabel={getSliderAltText(props.sliderAriaLabel, value)}
    />
  );
};

export default (props) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <SliderImage {...props} />
  </TranslationsProvider>
);
